// components/portal/BrandExpansionChart.tsx
// Expansion-rate graph for the brand detail "Expansion" tab: France store openings (up) and
// closures (down) per year, from the OFFICIAL INSEE SIRENE establishment history, exact dated,
// churn-corrected, SIREN-anchored (scripts/brands/ingest-openings-sirene.ts, served from the
// persisted signal so it renders instantly). We only draw a series when it is authoritative:
// a brand with no verified FR legal-entity anchor shows the "link a legal entity" state rather
// than another company's name-matched stores.
'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ResponsiveBar, type BarCustomLayerProps, type BarDatum } from '@nivo/bar';
import { useTooltip } from '@nivo/tooltip';
// preprod's guard: a Nivo throw must not take the whole app down (308abdef0).
import { ChartErrorBoundary } from '@/components/ui/ChartErrorBoundary';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import type { BrandExpansionPeriod, BrandExpansionSignal, BrandExpansionSignalState, BrandOpeningsEvidence, BrandRuntimeState } from '#types/types';
import { expansionChartYears } from '#lib/leasing/expansionChartYears';
import { PILL_SELECT_STYLE } from '@/components/portal/panelButton';
import { FranceOnlyChip } from '@/components/ui/FranceOnlyChip';

const PANEL_FONT = 'ui-sans-serif, -apple-system, system-ui, sans-serif';

// Greyscale glass. Openings and closures carry NO hue, so the two series are separated by
// POSITION (above / below the zero line) and by VALUE: openings are lit glass (bright fill +
// a 1px specular highlight on their rounded top edge), closures are unlit frosted glass
// (dim fill, no highlight, a faint rim so the panel edge still reads on the near-black surface).
// Flat since 2026-09-28 (owner: ChatGPT's philosophy): one solid grey per series, no gradient, rim
// or specular line. The glass plumbing stays so the two series keep one drawing path.
const GLASS = {
  openings: {
    top: 'rgba(255,255,255,0.82)',
    bottom: 'rgba(255,255,255,0.82)',
    rim: 'transparent',
    highlight: null as string | null,
  },
  closures: {
    top: 'rgba(255,255,255,0.24)',
    bottom: 'rgba(255,255,255,0.24)',
    rim: 'transparent',
    highlight: null as string | null,
  },
} as const;

const BAR_RADIUS = 6;
const GRAD_OPEN = 'brandExpansionGlassOpen';
const GRAD_CLOSE = 'brandExpansionGlassClose';

// The bar silhouette. A lone bar rounds on all four corners: it is a whole object, and squaring it
// off at the axis reads as a shape that has been cut. The radius is clamped to half the HEIGHT as
// well as half the width, so a one-unit bar becomes a lozenge instead of overshooting itself.
// See `joined` below for the year that carries both series.
const glassPath = (w: number, h: number, up: boolean, joined: boolean) => {
  const r = Math.max(0, Math.min(BAR_RADIUS, w / 2, h / 2));
  // A year with BOTH series is one object with the axis running through it, not two pills stacked
  // nose to nose. So the halves round only at their far ends and stay square where they meet, and
  // the pair reads as a single rounded column. A year with one series is a standalone shape and
  // rounds on all four corners.
  if (joined) {
    return up
      ? `M0,${h} L0,${r} A${r},${r} 0 0 1 ${r},0 L${w - r},0 A${r},${r} 0 0 1 ${w},${r} L${w},${h} Z`
      : `M0,0 L0,${h - r} A${r},${r} 0 0 0 ${r},${h} L${w - r},${h} A${r},${r} 0 0 0 ${w},${h - r} L${w},0 Z`;
  }
  return (
    `M${r},0 L${w - r},0 A${r},${r} 0 0 1 ${w},${r} ` +
    `L${w},${h - r} A${r},${r} 0 0 1 ${w - r},${h} ` +
    `L${r},${h} A${r},${r} 0 0 1 0,${h - r} ` +
    `L0,${r} A${r},${r} 0 0 1 ${r},0 Z`
  );
};

/** The 1px specular line that runs along the rounded outer edge (openings only). */
const highlightPath = (w: number, h: number) => {
  const r = Math.max(0, Math.min(BAR_RADIUS, w / 2, h / 2));
  return `M0.5,${r} A${r},${r} 0 0 1 ${r},0.5 L${w - r},0.5 A${r},${r} 0 0 1 ${w - 0.5},${r}`;
};

/** Legend chip: same lit / unlit glass and the same full rounding as the bar it stands for. */
const GlassSwatch = ({ up }: { up: boolean }) => (
  <span aria-hidden className="inline-block h-2 w-2 rounded-full" style={{ background: up ? GLASS.openings.top : GLASS.closures.top }} />
);

const GlassDefs = () => (
  <defs>
    <linearGradient id={GRAD_OPEN} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={GLASS.openings.top} />
      <stop offset="100%" stopColor={GLASS.openings.bottom} />
    </linearGradient>
    <linearGradient id={GRAD_CLOSE} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={GLASS.closures.top} />
      <stop offset="100%" stopColor={GLASS.closures.bottom} />
    </linearGradient>
  </defs>
);

/**
 * Bars layer replacing nivo's default rects: same nivo geometry, drawn as rounded frosted glass
 * panels (translucent vertical gradient + rim + outer shadow) instead of flat blocks.
 */
const GlassBars = ({ bars, fr }: BarCustomLayerProps<Row & BarDatum> & { fr: boolean }) => {
  const { showTooltipFromEvent, hideTooltip } = useTooltip();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  // Which years carry BOTH an opening and a closing bar. Those two halves have to be drawn as one
  // column, so each half needs to know the other is there before it decides how to round itself.
  const joinedYears = new Set<string>();
  const seen = new Map<string, boolean>();
  for (const bar of bars) {
    const v = Number(bar.data.value ?? 0);
    if (!v || bar.height < 0.5 || bar.width <= 0) continue;
    const year = String(bar.data.indexValue);
    const isUp = v > 0;
    const other = seen.get(year);
    if (other !== undefined && other !== isUp) joinedYears.add(year);
    else seen.set(year, isUp);
  }

  return (
    <g>
      {bars.map((bar) => {
        const value = Number(bar.data.value ?? 0);
        if (!value || bar.height < 0.5 || bar.width <= 0) return null;
        const up = value > 0;
        const joined = joinedYears.has(String(bar.data.indexValue));
        const skin = up ? GLASS.openings : GLASS.closures;
        const hovered = hoveredKey === bar.key;
        const label = up ? (fr ? 'ouvertures' : 'openings') : fr ? 'fermetures' : 'closures';
        const onMove = (event: React.MouseEvent<SVGGElement>) =>
          showTooltipFromEvent(
            <div
              style={{
                // nivo only positions the tooltip node; the chip has to carry its own surface.
                // The app's hover label: a small dark pill, medium white.
                padding: '4px 10px',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: PANEL_FONT,
                color: '#FFFFFF',
                whiteSpace: 'nowrap',
                background: '#353535',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
              }}
            >
              {bar.data.indexValue} · {Math.abs(value)} {label}
            </div>,
            event,
          );
        return (
          <g
            key={bar.key}
            transform={`translate(${bar.x},${bar.y})`}
            style={{ cursor: 'default' }}
            onMouseEnter={(event) => { setHoveredKey(bar.key); onMove(event); }}
            onMouseMove={onMove}
            onMouseLeave={() => { setHoveredKey(null); hideTooltip(); }}
          >
            <path
              d={glassPath(bar.width, bar.height, up, joined)}
              fill={`url(#${up ? GRAD_OPEN : GRAD_CLOSE})`}
              stroke={skin.rim}
              strokeWidth={1}
            />
            {hovered ? (
              <path d={glassPath(bar.width, bar.height, up, joined)} fill="rgba(255,255,255,0.12)" />
            ) : null}
            {skin.highlight && bar.height > 2 ? (
              <path
                d={highlightPath(bar.width, bar.height)}
                fill="none"
                stroke={skin.highlight}
                strokeWidth={1}
                strokeLinecap="round"
              />
            ) : null}
          </g>
        );
      })}
    </g>
  );
};

type Row = { year: string; openings: number; closures: number };

// Nivo recomputes its whole geometry when a prop identity changes: every one of these is fixed for
// the life of the module, so it is hoisted out of the render instead of being rebuilt per frame.
const BAR_KEYS = ['openings', 'closures'];
const CHART_MARGIN = { top: 16, right: 14, bottom: 28, left: 40 };
const AXIS_BOTTOM = { tickSize: 0, tickPadding: 8 };
const PERIODS = ['3y', '6y', '10y', 'all'] as const;
const ZERO_MARKERS = [
  { axis: 'y' as const, value: 0, lineStyle: { stroke: 'rgba(255,255,255,0.14)', strokeWidth: 1 } },
];
const CHART_THEME = {
  axis: { ticks: { text: { fill: '#8E8E8E', fontSize: 12, fontFamily: PANEL_FONT } } },
  grid: { line: { stroke: 'rgba(255,255,255,0.05)', strokeWidth: 1 } },
};
/** Closures are stored negative so they stack below the axis; the tick still reads as a count. */
const formatAbsolute = (v: string | number | Date) => String(Math.abs(Number(v)));

// Same greyscale ladder as the bars: the brighter the pill, the more expansive the status.
const STATUS_STYLE: Record<string, { bg: string; fg: string; border: string; en: string; fr: string }> = {
  EXPANDING: { bg: 'rgba(255,255,255,0.14)', fg: '#F2F2F2', border: 'rgba(255,255,255,0.22)', en: 'Expanding', fr: 'En expansion' },
  STABLE: { bg: 'rgba(255,255,255,0.09)', fg: '#CFCFCF', border: 'rgba(255,255,255,0.14)', en: 'Stable', fr: 'Stable' },
  STALE: { bg: 'rgba(255,255,255,0.06)', fg: '#A8A8A8', border: 'rgba(255,255,255,0.11)', en: 'Stalled', fr: 'À l’arrêt' },
  SHRINKING: { bg: 'rgba(255,255,255,0.05)', fg: '#949494', border: 'rgba(255,255,255,0.09)', en: 'Shrinking', fr: 'En repli' },
  EXITED: { bg: 'rgba(255,255,255,0.04)', fg: '#858585', border: 'rgba(255,255,255,0.08)', en: 'Left the market', fr: 'A quitté le marché' },
  UNKNOWN: { bg: 'rgba(255,255,255,0.05)', fg: '#8A8A8A', border: 'rgba(255,255,255,0.09)', en: 'Unknown', fr: 'Inconnu' },
};

export const BrandExpansionChart = ({ brandId, lang }: { brandId?: string | null; lang: string }) => {
  const fr = lang === 'fr';
  const { kernel } = useKernel();

  // The signal is a derived brand sub-resource cached on the brand's runtime overlay by the
  // kernel (see loadBrandExpansionSignal). Reading it here rather than fetching keeps the
  // component free of its own I/O pipeline, and keeps a re-open of the tab from re-querying.
  const expansion = useKernelStore((state) => {
    if (!brandId) return undefined;
    const entry = state.entityKernelState[`Brand:${brandId}`]?.state as
      | { runtime?: { expansionSignal?: BrandExpansionSignalState } }
      | undefined;
    return entry?.runtime?.expansionSignal;
  });
  const period = useKernelStore((state) => {
    const entry = state.entityKernelState[`Brand:${brandId}`]?.state as { runtime?: BrandRuntimeState } | undefined;
    return entry?.runtime?.expansionPeriod ?? '6y';
  });
  const selectPeriod = useCallback((event: React.MouseEvent<HTMLButtonElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const id = event.currentTarget.dataset.brandId;
    const value = event.currentTarget.value;
    if (!id || !value) return;
    const next: BrandExpansionPeriod = PERIODS.includes(value as typeof PERIODS[number])
      ? value as typeof PERIODS[number]
      : Number(value);
    kernel.current?.patchEntityRuntimeState('Brand', id, { expansionPeriod: next });
  }, [kernel]);

  useEffect(() => {
    if (!brandId) return;
    // The kernel skips the call when this brand's signal is already loading or loaded.
    void kernel.current?.loadBrandExpansionSignal(brandId);
  }, [brandId, kernel]);

  const data = expansion?.status === 'loaded' ? expansion.signal : null;
  const brandName = useKernelStore((state) => (brandId ? state.inventory.Brand.registry[brandId]?.name ?? null : null));
  const [showSources, setShowSources] = useState(false);
  const failed = expansion?.status === 'failed';
  // Not yet on the overlay = the load is about to fire, which is the same thing on screen.
  const loading = !!brandId && (expansion === undefined || expansion.status === 'loading');

  const rows = useMemo<Row[]>(
    () => expansionChartYears(data?.openingsByYear, data?.closuresByYear, period)
      .map(({ year, openings, closures }) => ({ year, openings, closures: -closures })),
    [data, period],
  );
  const allYears = useMemo(
    () => expansionChartYears(data?.openingsByYear, data?.closuresByYear, 'all').map((row) => row.year),
    [data],
  );

  // Stable identity: nivo remounts a custom layer whose component identity changes on every render.
  const layers = useMemo(
    () => [
      'grid' as const,
      'axes' as const,
      GlassDefs,
      (props: BarCustomLayerProps<Row & BarDatum>) => <GlassBars {...props} fr={fr} />,
      'markers' as const,
    ],
    [fr],
  );

  // The value scale must be pinned by hand: left on `auto`, nivo derives the domain from the
  // positive stack only, so the closures (negative) bars are laid out BELOW the plot area and
  // land on top of the year labels. Openings and closures are counts, so the ticks are whole
  // numbers, the previous auto ticks read "0.5 openings".
  const scale = useMemo(() => {
    const max = Math.max(1, ...rows.map((r) => r.openings));
    const min = Math.min(0, ...rows.map((r) => r.closures));
    const step = Math.max(1, Math.ceil((max - min) / 4));
    // Just enough headroom for the outer end of the tallest bar; rounding the domain out to a whole
    // step instead would leave a quarter of the plot empty when a brand has a single closure.
    const pad = (max - min) * 0.08;
    const top = max + pad;
    const bottom = min === 0 ? 0 : min - pad;
    const ticks: number[] = [];
    for (let v = 0; v <= top; v += step) ticks.push(v);
    for (let v = -step; v >= bottom; v -= step) ticks.unshift(v);
    // Handed to nivo as-is: the derived props live in the memo so they keep the identity of the
    // scale they describe, instead of being rebuilt inline on every render of the tab.
    return {
      ticks,
      valueScale: { type: 'linear' as const, min: bottom, max: top },
      axisLeft: { tickSize: 0, tickPadding: 6, tickValues: ticks, format: formatAbsolute },
    };
  }, [rows]);

  const hasSeries = rows.some((r) => r.openings !== 0 || r.closures !== 0);
  const authoritative = data?.authoritative === true;

  if (loading) {
    return (
      <div className="skeleton-in space-y-6" aria-busy="true" role="status" aria-label={fr ? 'Chargement' : 'Loading'}>
        <div className="grid grid-cols-3 gap-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-[92px] rounded-[16px]" />)}</div>
        <div className="skeleton h-[300px] rounded-[20px]" />
      </div>
    );
  }

  // No authoritative signal → never show name-matched or stale numbers. Say exactly why.
  if (!authoritative) {
    const msg = failed
      ? (fr ? 'Signal momentanément indisponible.' : 'Signal momentarily unavailable.')
      : data?.hasFrenchPresence === false
        ? (fr
            ? 'Cette marque n’opère pas de points de vente en France, pas de dynamique d’ouvertures française à afficher.'
            : 'This brand does not operate stores in France, no French opening cadence to show.')
        : (fr
            ? 'Aucun établissement français trouvé dans le registre officiel (ni sous son entité légale, ni sous son enseigne). Enseigne à rattacher, ou point de vente introuvable au registre.'
            : 'No French establishment found in the official registry (neither under its legal entity nor its storefront sign). Its retail banner may need linking, or the store isn’t registered under a matchable name.');
    return (
      <div className="flex h-[300px] items-center justify-center rounded-[20px] border border-[#262626] bg-[#1B1B1B] px-8 text-center text-[13px] leading-relaxed text-[#8E8E8E]">
        {msg}
      </div>
    );
  }

  const status = (data?.status ?? 'UNKNOWN') as string;
  const st = STATUS_STYLE[status] ?? STATUS_STYLE.UNKNOWN!;
  const o3 = data?.openings3y ?? 0;
  const c3 = data?.closures3y ?? 0;
  const net = data?.netOpenings3y ?? (o3 - c3);
  const calcYear = data?.calculatedAt ? new Date(data.calculatedAt).toISOString().slice(0, 7) : null;

  return (
    <div
      // The Launch bar's card (owner, 2026-09-28): one flat #212121 panel, no header rule.
      className="overflow-hidden rounded-[20px] border border-[#262626] bg-[#1B1B1B]"
    >
      {/* Status and source on one line, the 3-year count on the next, each figure kept whole when the
          line wraps: the card reads the same in a side panel as on a full page. */}
      <div className="flex flex-col gap-2 px-5 pb-1 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* A dot and a word, as every status in the app now reads (owner, 2026-09-28). */}
          <span className="flex items-center gap-2 font-medium text-white" style={{ fontSize: 14 }}>
            <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: st.fg }} />
            <span style={{ fontSize: 14 }}>{fr ? st.fr : st.en}</span>
          </span>
          <span className="flex items-center gap-2" style={{ fontSize: 12, color: '#8E8E8E' }}>
            <FranceOnlyChip lang={lang} />
            {data?.source === 'WEB_RESEARCH' ? (fr ? 'Recherche web sourcée' : 'Sourced web research') : data?.evidence ? (
              <button
                type="button"
                onClick={() => setShowSources((open) => !open)}
                className="h-7 whitespace-nowrap rounded-full bg-white/[0.06] px-3 text-[#E5E5E5] transition-colors hover:bg-white/[0.1]"
                style={{ fontSize: 12 }}
                title={fr ? 'Voir les détails conservés des établissements et leur source officielle' : 'See retained establishment details and their official source'}
              >
                {fr ? 'Source : INSEE SIRENE' : 'Source: INSEE SIRENE'} {showSources ? '▴' : '▾'}
              </button>
            ) : 'INSEE SIRENE'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1" style={{ fontSize: 13 }}>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><GlassSwatch up /><span style={{ fontSize: 13, color: '#E5E5E5' }}>+{o3}</span><span style={{ fontSize: 13, color: '#8E8E8E' }}>{fr ? 'ouvertures' : 'openings'}</span></span>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><GlassSwatch up={false} /><span style={{ fontSize: 13, color: '#E5E5E5' }}>−{c3}</span><span style={{ fontSize: 13, color: '#8E8E8E' }}>{fr ? 'fermetures' : 'closures'}</span></span>
          <span className="whitespace-nowrap"><span style={{ fontSize: 13, color: '#FFFFFF' }}>{fr ? 'solde' : 'net'} {net >= 0 ? '+' : ''}{net}</span> <span style={{ fontSize: 13, color: '#8E8E8E' }}>{fr ? 'sur 3 ans' : 'over 3 years'}</span></span>
        </div>
        {(calcYear || data?.lastOpeningDate) && <span style={{ fontSize: 12, color: '#8E8E8E' }}>
          {[calcYear, data?.lastOpeningDate ? `${fr ? 'dernière ouverture' : 'last opening'} ${data.lastOpeningDate}` : null].filter(Boolean).join(' · ')}
        </span>}
      </div>

      <ExpansionContext data={data} brandName={brandName} fr={fr} />

      <div role="group" aria-label={fr ? 'Période du graphique' : 'Chart time horizon'} className="flex flex-wrap items-center gap-1 px-4 pt-3">
        {PERIODS.map((choice) => (
          <button
            key={choice}
            type="button"
            value={choice}
            data-brand-id={brandId}
            onClick={selectPeriod}
            aria-pressed={period === choice}
            className={`h-8 rounded-full px-3 transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9A9A9A] ${period === choice ? 'bg-white/[0.08] text-white' : 'text-[#B4B4B4] hover:bg-white/[0.05] hover:text-white'}`}
            style={{ fontSize: 13 }}
          >
            {choice === 'all' ? (fr ? 'Toutes les années' : 'All years') : `${choice.slice(0, -1)} ${fr ? 'ans' : 'years'}`}
          </button>
        ))}
        <select
          aria-label={fr ? 'Année enregistrée' : 'Recorded year'}
          data-brand-id={brandId}
          value={typeof period === 'number' ? String(period) : ''}
          onChange={selectPeriod}
          className="ml-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9A9A9A]"
          style={PILL_SELECT_STYLE}
        >
          <option value="" disabled>{fr ? 'Une année…' : 'One year…'}</option>
          {allYears.map((year) => <option key={year} value={year}>{year}</option>)}
        </select>
        <span className="ml-2 text-[#8E8E8E]" style={{ fontSize: 13 }} aria-live="polite">
          {rows.length ? `${rows[0]!.year}${rows.length > 1 ? `–${rows[rows.length - 1]!.year}` : ''}` : ''}
        </span>
      </div>

      {hasSeries ? (
        <div className="overflow-x-auto" role="region" aria-label={fr ? 'Graphique des ouvertures et fermetures' : 'Openings and closures chart'} tabIndex={0}>
        <div className="h-[260px] w-full p-2" style={{ minWidth: Math.max(240, rows.length * 42 + 54) }}>
          <ChartErrorBoundary label="Expansion">
          <ResponsiveBar
            data={rows}
            keys={BAR_KEYS}
            indexBy="year"
            margin={CHART_MARGIN}
            padding={0.35}
            groupMode="stacked"
            valueScale={scale.valueScale}
            enableLabel={false}
            layers={layers}
            axisBottom={AXIS_BOTTOM}
            axisLeft={scale.axisLeft}
            enableGridY
            gridYValues={scale.ticks}
            markers={ZERO_MARKERS}
            theme={CHART_THEME}
          />
          </ChartErrorBoundary>
        </div>
        </div>
      ) : (
        <div className="flex h-[240px] items-center justify-center px-6 text-center italic text-[#6E6E6E]" style={{ fontSize: 12 }}>
          {fr ? 'Aucune ouverture ou fermeture comptée sur la période.' : 'No openings or closures counted for the period.'}
        </div>
      )}
      {/* THE SOURCE, LINKED, like the revenue panel links its filing: the series is the
          establishment history of these companies in the national register. */}
      {data?.sources && data.sources.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-white/[0.06] px-4 py-2" style={{ fontSize: 10, color: '#6E6E6E' }}>
          <span>{fr ? 'Sources :' : 'Sources:'}</span>
          {data.sources.slice(0, 6).map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer" className="underline decoration-[#3A3A3A] underline-offset-2 hover:text-[#9A9A9A]">
              {(() => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; } })()}
            </a>
          ))}
        </div>
      ) : data?.sirens && data.sirens.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-white/[0.06] px-4 py-2" style={{ fontSize: 10, color: '#6E6E6E' }}>
          <span>{fr ? 'Source : registre INSEE SIRENE, établissements de' : 'Source: INSEE SIRENE register, establishments of'}</span>
          {data.sirens.map((siren) => (
            <a
              key={siren}
              href={`https://annuaire-entreprises.data.gouv.fr/entreprise/${siren}`}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-[#3A3A3A] underline-offset-2 hover:text-[#9A9A9A]"
            >
              {data.companies?.find((c) => c.siren === siren)?.name ?? `SIREN ${siren}`}
            </a>
          ))}
        </div>
      ) : null}
      {showSources && data?.evidence ? (
        <ExpansionSources evidence={data.evidence} years={rows.map((r) => Number(r.year)).filter(Number.isFinite)} fr={fr} />
      ) : null}
    </div>
  );
};

BrandExpansionChart.displayName = 'BrandExpansionChart';

// WHAT THE SERIES COUNTS, said like the revenue card says whose accounts it shows (owner,
// 2026-10-04): French shops from the national register, whose they are, and how much of the
// network we hold they cover. A register series under half the network says what it misses.
const ExpansionContext = ({ data, brandName, fr }: { data: BrandExpansionSignal | null; brandName: string | null; fr: boolean }) => {
  if (!data) return null;
  if (data.source === 'WEB_RESEARCH') {
    return <p className="px-5 pt-2 text-[#8E8E8E]" style={{ fontSize: 13 }}>{fr ? 'Ouvertures et fermetures en France relevées dans les pages citées plus bas, faute de série au registre.' : 'French openings and closures read from the pages cited below, as the register holds no series for this brand.'}</p>;
  }
  const names = (data.companies ?? []).map((c) => c.name ?? `SIREN ${c.siren}`);
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} ${fr ? 'et' : 'and'} ${names.at(-1)}` : names[0] ?? null;
  const brand = brandName ?? (fr ? 'la marque' : 'the brand');
  const open = data.openEstablishments, stores = data.frenchStores;
  const coverage = open != null && stores
    ? fr
      ? ` ${open} sont ouverts aujourd’hui, pour ${stores} magasins en France dans nos données.${open < stores / 2 ? ' Les magasins franchisés que le registre ne rattache pas à la marque ne sont pas comptés.' : ''}`
      : ` ${open} are open today, against the ${stores} French stores we hold.${open < stores / 2 ? ' Franchised shops the register does not tie to the brand are not counted.' : ''}`
    : '';
  return (
    <p className="px-5 pt-2 text-[#8E8E8E]" style={{ fontSize: 13 }}>
      {fr
        ? `Magasins immatriculés en France (registre INSEE SIRENE)${list ? ` : ceux de ${list}, et ceux que d’autres sociétés exploitent sous l’enseigne ${brand} ou à ses adresses` : ''}.`
        : `Shops registered in France (INSEE SIRENE register)${list ? `: those of ${list}, and those other companies run under the ${brand} sign or at its store addresses` : ''}.`}
      {coverage}
    </p>
  );
};


const DIRECTORY = 'https://annuaire-entreprises.data.gouv.fr';
const SOURCE_ROWS_SHOWN = 200;
const linkClass = 'text-[#3B82F6] hover:underline';

const ExpansionSources = ({ evidence, years, fr }: { evidence: BrandOpeningsEvidence; years: number[]; fr: boolean }) => {
  const rows = useMemo(
    () => {
      const selectedYears = new Set(years);
      const matchesPeriod = (date: string | null) => {
        if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
        const parsed = new Date(date);
        return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date
          && selectedYears.has(parsed.getUTCFullYear());
      };
      return evidence.establishments.filter((e) => matchesPeriod(e.opened) || matchesPeriod(e.closed));
    },
    [evidence.establishments, years],
  );
  const bulk = evidence.method === 'SIRENE_HISTORIQUE';
  return (
    <div className="border-t border-white/[0.06] px-4 py-3" style={{ fontSize: 12 }}>
      <div className="mb-2 text-[#B5B5B5]">
        {fr ? 'Source officielle : ' : 'Official source: '}
        <a href={evidence.datasetUrl} target="_blank" rel="noreferrer" className={linkClass}>
          {bulk ? 'INSEE SIRENE, historique des établissements' : 'Annuaire des Entreprises (API recherche-entreprises, INSEE SIRENE)'}
        </a>
        {evidence.release ? <span className="text-[#6E6E6E]"> · {bulk ? (fr ? 'édition' : 'release') : (fr ? 'consulté le' : 'queried')} {evidence.release}</span> : null}
      </div>
      {evidence.sirens.length > 0 ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[#9A9A9A]">
          <span>{fr ? 'Société(s) :' : 'Company:'}</span>
          {evidence.sirens.slice(0, 8).map((siren) => (
            <a key={siren} href={`${DIRECTORY}/entreprise/${siren}`} target="_blank" rel="noreferrer" className={`font-mono ${linkClass}`}>{siren}</a>
          ))}
        </div>
      ) : null}
      <div className="mb-2 text-[#8A8A8A]" style={{ fontSize: 11 }} aria-live="polite">
        {rows.length} {fr ? 'établissement(s) avec une date enregistrée sur la période' : 'establishment(s) with a recorded date in this period'}
        {rows.length > SOURCE_ROWS_SHOWN ? ` · ${SOURCE_ROWS_SHOWN} / ${rows.length} ${fr ? 'affichés' : 'shown'}` : ''}
      </div>
      <p className="mb-2 text-[11px] text-[#8A8A8A]">
        {fr
          ? 'Les dates enregistrées peuvent inclure des événements exclus des totaux du graphique.'
          : 'Recorded dates may include events excluded from the chart totals.'}
      </p>
      {evidence.total > evidence.establishments.length ? (
        <p className="mb-2 text-[11px] text-[#8A8A8A]">
          {fr
            ? `Détail partiel : ${evidence.establishments.length} établissements conservés sur ${evidence.total}. Le graphique utilise les totaux annuels complets.`
            : `Partial source detail: ${evidence.establishments.length} of ${evidence.total} establishments retained. The chart uses the full annual counts.`}
        </p>
      ) : null}
      {rows.length === 0 ? <p className="mb-2 text-[11px] text-[#8A8A8A]">{fr ? 'Aucun détail de source conservé pour cette période.' : 'No retained source details for this period.'}</p> : null}
      <div className="max-h-[220px] overflow-y-auto">
        <table className="w-full text-left" style={{ fontSize: 11 }}>
          <thead className="text-[#6E6E6E]">
            <tr><th className="py-1 pr-4 font-normal">SIRET</th><th className="py-1 pr-4 font-normal">{fr ? 'Ouverture' : 'Opened'}</th><th className="py-1 font-normal">{fr ? 'Fermeture' : 'Closed'}</th></tr>
          </thead>
          <tbody className="text-[#CFCFCF]">
            {rows.slice(0, SOURCE_ROWS_SHOWN).map((e) => (
              <tr key={e.siret} className="border-t border-white/[0.04]">
                <td className="py-1 pr-4"><a href={`${DIRECTORY}/etablissement/${e.siret}`} target="_blank" rel="noreferrer" className={`font-mono ${linkClass}`}>{e.siret}</a></td>
                <td className="py-1 pr-4 font-mono">{e.opened ?? '-'}</td>
                <td className="py-1 font-mono">{e.closed ?? (e.isClosed ? (fr ? 'fermé' : 'closed') : '-')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
